/**
 * React Query hooks for hive-level skill bindings.
 *
 * Sprint 133 Stream C — feeds the HiveDetailPage Skills tab,
 * M-H1 (HiveSkillAddModal) and M-H2 (HiveSkillInheritanceModal).
 *
 * All paths are tenant-scoped — built explicitly from the tenant context.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';
import type {
  AgentSkillBindingCreate,
  HiveInheritancePreviewResponse,
  HiveSkillBinding,
  HiveSkillBindingCreate,
} from '../types';

const HIVE_SKILLS_KEY = 'hive-skills';
const HIVE_INHERITANCE_KEY = 'hive-skill-inheritance';

function useHiveTenantId(): string | undefined {
  const { currentTenant } = useTenant();
  return currentTenant?.id;
}

export function useHiveSkillBindings(hiveId: string | undefined) {
  const tenantId = useHiveTenantId();
  return useQuery({
    queryKey: [HIVE_SKILLS_KEY, tenantId, hiveId],
    queryFn: async () => {
      if (!tenantId || !hiveId) throw new Error('No tenant selected');
      const { data } = await apiClient.get<HiveSkillBinding[]>(
        `/api/v1/tenants/${tenantId}/hives/${hiveId}/skills`,
      );
      return data;
    },
    enabled: !!hiveId && !!tenantId,
  });
}

export function useUpsertHiveSkillBinding(hiveId: string) {
  const qc = useQueryClient();
  const tenantId = useHiveTenantId();
  return useMutation({
    mutationFn: async (payload: HiveSkillBindingCreate) => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.put<HiveSkillBinding>(
        `/api/v1/tenants/${tenantId}/hives/${hiveId}/skills/${payload.skill_id}`,
        payload satisfies AgentSkillBindingCreate,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [HIVE_SKILLS_KEY, hiveId] });
      qc.invalidateQueries({ queryKey: [HIVE_INHERITANCE_KEY, hiveId] });
    },
  });
}

export function useDeleteHiveSkillBinding(hiveId: string) {
  const qc = useQueryClient();
  const tenantId = useHiveTenantId();
  return useMutation({
    mutationFn: async (skillId: string) => {
      if (!tenantId) throw new Error('No tenant selected');
      await apiClient.delete(
        `/api/v1/tenants/${tenantId}/hives/${hiveId}/skills/${skillId}`,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [HIVE_SKILLS_KEY, hiveId] });
      qc.invalidateQueries({ queryKey: [HIVE_INHERITANCE_KEY, hiveId] });
    },
  });
}

/**
 * Preview how the hive's bindings project onto a single member agent.
 * Used by **M-H2** (HiveSkillInheritanceModal).
 */
export function useHiveInheritancePreview(
  hiveId: string | undefined,
  agentId: string | undefined,
) {
  const tenantId = useHiveTenantId();
  return useQuery({
    queryKey: [HIVE_INHERITANCE_KEY, tenantId, hiveId, agentId],
    queryFn: async () => {
      if (!tenantId || !hiveId || !agentId) throw new Error('No tenant selected');
      const { data } = await apiClient.get<HiveInheritancePreviewResponse>(
        `/api/v1/tenants/${tenantId}/hives/${hiveId}/skills/inheritance-preview?agent_id=${agentId}`,
      );
      return data;
    },
    enabled: !!hiveId && !!agentId && !!tenantId,
  });
}
